import * as React from 'react';

interface Point {
  x: number;
  y: number;
}

interface Props {
  /**
   * Callback to return the dimensions of the content of this component.
   */
  onSize?: (dimensions: Point) => void;
  /**
   * Callback to resize the window when the content of this component as changed.
   */
  onResize?: (dimensions: Point) => void;
  /**
   * Callback to modify the window size without changing the base size.
   */
  onModifySize?: (dimensions: Point) => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
  mode: MeasureMode;
}

export enum MeasureMode {
  /**
   * Children can change size at after the initial render.
   */
  CONSTANT,
  /**
   * Children never change after initial render.
   */
  ONCE,
}

interface ResizeContextValue {
  /**
   * Emit an event that the content has changed.
   * @param additional Add a value to the measurement for animations in progress.
   */
  emitResize: (additional?: number) => void;
  /**
   * Emit an event that the content has changed.
   */
  emitModify: (amount?: Point) => void;
}

export const ResizeContext = React.createContext<ResizeContextValue>({} as ResizeContextValue);

const baseStyle: React.CSSProperties = {
  margin: '0',
  display: 'inline-block',
};

export const ResizeWrapper: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { children, onModifySize, onSize, mode, onResize } = props;
  const style = { ...baseStyle, ...props.style };
  const nodeRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(
    () => {
      const node = nodeRef.current;
      if (!node) {
        return;
      }
      // Get the size of the container ref
      const updateWindowDimensions = () => {
        const { width, height } = node.getBoundingClientRect();
        if (width && height) {
          if (onSize) {
            onSize({ x: width, y: height });
          }
        }
      };

      // Wait for all images to load or fail and then size the window
      const imgs = Array.from(node.getElementsByTagName('img'));
      Promise.all(
        imgs.map((img) => {
          return new Promise<void>((res) => {
            if (img.complete && img.naturalWidth > 0) {
              res();
              return;
            }

            img.addEventListener('load', () => res(), { once: true });
            img.addEventListener('error', () => res(), { once: true });
          });
        })
      )
        // @ts-ignore
        .then(() => updateWindowDimensions());
    },
    mode === MeasureMode.ONCE ? [] : [children]
  );

  const resizeContextValue: ResizeContextValue = {
    emitResize: (additional?: number) => {
      if (!nodeRef.current || !onResize) {
        return;
      }
      const { width, height } = nodeRef.current.getBoundingClientRect();
      if (width && height) {
        onResize({ x: width, y: height + (additional ?? 0) });
      }
    },
    emitModify: (amount?: Point) => {
      onModifySize?.(amount ?? { x: 0, y: 0 });
    },
  };

  return (
    <ResizeContext.Provider value={resizeContextValue}>
      <div ref={nodeRef} style={style}>
        {React.Children.only(children)}
      </div>
    </ResizeContext.Provider>
  );
};
